import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  CheckCircle,
  AlertTriangle,
  Users,
  Calendar,
  BarChart3,
  FileText,
  Wrench,
  ArrowUpRight,
} from 'lucide-react';

export const AdminDashboardHero = ({ userName }) => {
  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-xl bg-gradient-to-r from-red-600 to-red-700 p-4 text-white shadow-lg sm:mb-8 sm:p-8"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-1 text-xl font-bold sm:mb-2 sm:text-3xl">Bonjour, {userName}</h1>
          <p className="text-sm text-red-100 sm:text-base">
            Bienvenue sur votre tableau de bord d'administration
          </p>
        </div>
        <div className="flex w-fit items-center gap-3 rounded-lg bg-white/10 px-3 py-2 backdrop-blur-sm sm:px-4 sm:py-3">
          <Calendar className="h-5 w-5" />
          <div className="text-sm">
            <div className="font-semibold capitalize">{today}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const kpiConfig = [
  {
    key: 'sites',
    label: 'Sites actifs',
    icon: MapPin,
    getValue: (s) => s.totalSites,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    key: 'operational',
    label: 'Équipements conformes',
    icon: CheckCircle,
    getValue: (s) => s.operational,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    key: 'maintenance',
    label: 'Maintenance requise',
    icon: AlertTriangle,
    getValue: (s) => s.maintenanceRequired,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    key: 'clients',
    label: 'Clients',
    icon: Users,
    getValue: (s) => s.totalClients,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
];

export const AdminKpiGrid = ({ stats }) => (
  <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:gap-4 lg:grid-cols-4">
    {kpiConfig.map((kpi, index) => (
      <motion.div
        key={kpi.key}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="min-w-0 rounded-xl bg-white p-3 shadow-md sm:p-6"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium leading-snug text-gray-600 sm:text-sm">{kpi.label}</p>
            <p className="mt-1 text-xl font-bold text-gray-900 sm:mt-2 sm:text-3xl">
              {kpi.getValue(stats)}
            </p>
          </div>
          <div className={`shrink-0 rounded-lg ${kpi.bgColor} p-2 sm:p-3 ${kpi.color}`}>
            <kpi.icon className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

const quickActions = [
  {
    title: 'Nouveau client',
    description: 'Créer une fiche client',
    icon: Users,
    path: '/clients/add',
  },
  {
    title: 'Gérer les sites',
    description: 'Ajouter ou modifier des sites',
    icon: MapPin,
    path: '/admin/sites',
  },
  {
    title: 'Planifier une maintenance',
    description: 'Créer une intervention',
    icon: Wrench,
    path: '/maintenance',
  },
  {
    title: 'Gérer les plans',
    description: 'Téléverser un plan',
    icon: BarChart3,
    path: '/plans',
  },
  {
    title: 'Générer un rapport',
    description: 'Créer un document',
    icon: FileText,
    path: '/reports',
  },
];

export const AdminQuickActions = ({ navigate }) => (
  <div className="rounded-xl bg-white p-6 shadow-md">
    <h2 className="mb-4 text-xl font-bold text-gray-900">Actions rapides</h2>
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
      {quickActions.map((action) => (
        <button
          key={action.path}
          onClick={() => navigate(action.path)}
          className="group flex min-w-0 flex-col items-start rounded-lg border border-gray-200 p-3 text-left transition-all hover:border-red-600 hover:shadow-md sm:p-4"
        >
          <div className="mb-3 flex w-full items-center justify-between">
            <div className="rounded-lg bg-red-50 p-2 text-red-600">
              <action.icon className="h-5 w-5" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-gray-400 transition-colors group-hover:text-red-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{action.title}</h3>
          <p className="mt-1 text-xs text-gray-600 sm:text-sm">{action.description}</p>
        </button>
      ))}
    </div>
  </div>
);

export const AdminSectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="rounded-lg bg-red-600 p-2 text-white">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
    </div>
  </div>
);
